/**
 * The outbox — a durable, ordered queue of local mutations awaiting sync.
 *
 * Every write in the app lands in IndexedDB and then here. Nothing in the UI
 * waits on the network; this queue is what eventually reconciles with the
 * server, whenever a network happens to exist.
 *
 * Why coalescing matters: autosave fires every 400ms on Final Review, and
 * China Notes autosaves as you type. A day of capture across 100 suppliers
 * would otherwise queue tens of thousands of entries, all but the last of each
 * record redundant. `coalesce` collapses them to one per record.
 */

import { add, all, delAll, put } from './db';
import { STORE } from './schema';

export const OP = { UPSERT: 'upsert', DELETE: 'delete' };
export const ENTITY = { FAIR: 'fair', SUPPLIER: 'supplier', SETTINGS: 'settings' };

/**
 * Queue a mutation. `payload` is the full record, not a patch — the records are
 * small, and a full snapshot replays correctly no matter how stale the queue is
 * or what order a retry lands in.
 */
export function enqueue({ entity, id, op = OP.UPSERT, payload }) {
  return add(STORE.OUTBOX, {
    entity,
    id,
    op,
    payload,
    queuedAt: Date.now(),
    tries: 0,
    lastError: null,
  });
}

/** Everything pending, in seq order. */
export async function pending() {
  const rows = await all(STORE.OUTBOX);
  return rows.sort((a, b) => a.seq - b.seq);
}

/**
 * Collapse the queue to one entry per (entity, id), keeping the LAST — which
 * for a full-snapshot payload is the only one that matters.
 *
 * A delete always wins over earlier upserts of the same record: once the buyer
 * has deleted a supplier, replaying an earlier upsert would resurrect it.
 *
 * Returns { send, drop }: `send` is what goes to the server in seq order,
 * `drop` the superseded seqs, which are cleared alongside the ones we sent.
 */
export function coalesce(rows) {
  const keep = new Map(); // "entity:id" → row
  const drop = [];

  for (const row of rows) {
    const key = `${row.entity}:${row.id}`;
    const prev = keep.get(key);
    if (prev) drop.push(prev.seq);
    // The later row supersedes, and a delete stays a delete even if something
    // upserted the record again afterwards (it cannot — delete is terminal in
    // the UI — but replay must not depend on that).
    if (prev && prev.op === OP.DELETE && row.op !== OP.DELETE) {
      keep.set(key, prev);
      drop.pop();
      drop.push(row.seq);
    } else {
      keep.set(key, row);
    }
  }

  const send = Array.from(keep.values()).sort((a, b) => a.seq - b.seq);
  return { send, drop };
}

/** Clear entries the server has accepted (plus any they superseded). */
export function settle(seqs) {
  return delAll(STORE.OUTBOX, seqs);
}

/**
 * Record a failed attempt so a poison entry can't block the queue forever.
 * Nothing is dropped automatically — losing a booth visit silently is worse
 * than a stuck queue, so this only counts, and Settings surfaces the count.
 */
export async function noteFailure(row, error) {
  await put(STORE.OUTBOX, {
    ...row,
    tries: (row.tries || 0) + 1,
    lastError: String((error && error.message) || error || 'unknown'),
  });
}

export async function pendingCount() {
  const rows = await all(STORE.OUTBOX);
  return rows.length;
}
